import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEarnFxSource } from '../../api/modules/components';
import type { EarnFxHookMeta } from '../../api/modules/components';
import { LOGICAL_H, LOGICAL_W } from './demo-utils';

declare global {
  interface Window {
    __PLAYGEN_STATE?: { reward?: Record<string, unknown> };
  }
}

const STYLES = ['classic', 'gold', 'jade', 'glass'] as const;

/** 钩子按钮的演示参数（与真实玩法调用点语义一致） */
const HOOK_ARGS: Record<string, unknown[]> = {
  efxOnAction: [170, 200, 1],
  efxOnCorrect: [170, 200, 300],
  efxOnCombo: [170, 180, 3],
  efxOnLevelUp: [2],
  efxOnWin: [],
  efxOnFail: [],
  efxOnMilestone: [0],
  efxOnUrgent: [],
  efxOnIdleTick: [50],
};

const EPILOGUE = `
;return {
  drawEarnFxLayer: drawEarnFxLayer,
  efxOnAction: efxOnAction,
  efxOnCorrect: efxOnCorrect,
  efxOnCombo: efxOnCombo,
  efxOnLevelUp: efxOnLevelUp,
  efxOnWin: efxOnWin,
  efxOnFail: efxOnFail,
  efxOnMilestone: efxOnMilestone,
  efxOnUrgent: efxOnUrgent,
  efxOnIdleTick: efxOnIdleTick,
  _efxPublishState: _efxPublishState
};`;

/** Earn-FX 活预览：mock DIFF/COLORS/IMG_SLOTS 注入红包层源码，驱动 9 个事件钩子 */
export function EarnFxPreview() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [style, setStyle] = useState<string>('classic');
  const [hooks, setHooks] = useState<EarnFxHookMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reward, setReward] = useState<Record<string, unknown> | null>(null);
  const apiRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    let disposed = false;
    let raf = 0;
    setError(null);
    apiRef.current = null;

    (async () => {
      try {
        const payload = await fetchEarnFxSource(style);
        if (disposed) return;
        const factory = new Function(
          'window', 'document', 'DIFF', 'COLORS', 'W', 'H', 'IMG_SLOTS',
          payload.source + EPILOGUE,
        );
        const api = factory(
          window, document, payload.config, ['#E8262A', '#F5A623'], LOGICAL_W, LOGICAL_H, {},
        );
        apiRef.current = api;
        setHooks(payload.hooks);

        // 图鉴打开时直接展示猜歌/成语共用的 classic 红包卡，避免只看到顶部
        // 的一条进度线而误以为红包没有接入。真实玩法仍由 efxOnCorrect 在答题
        // 成功时触发，这里只是确定性的预览初始状态。
        api.efxOnCorrect?.(LOGICAL_W / 2, LOGICAL_H * 0.46, 1000);

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx || disposed) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = LOGICAL_W * dpr;
        canvas.height = LOGICAL_H * dpr;
        const loop = () => {
          if (disposed) return;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, LOGICAL_W, LOGICAL_H);
          try {
            api.drawEarnFxLayer?.(ctx, LOGICAL_W, LOGICAL_H);
            api._efxPublishState?.();
          } catch {
            /* 单帧绘制异常不中断循环 */
          }
          setReward(window.__PLAYGEN_STATE?.reward ?? null);
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      } catch (e) {
        if (!disposed) setError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
    };
  }, [style]);

  const fire = useCallback((name: string) => {
    const fn = apiRef.current?.[name];
    if (typeof fn === 'function') fn(...(HOOK_ARGS[name] || []));
  }, []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative rounded-xl border border-slate-700/60 bg-[#0d1220] overflow-hidden" style={{ width: LOGICAL_W, height: LOGICAL_H }}>
        <canvas ref={canvasRef} style={{ width: LOGICAL_W, height: LOGICAL_H }} />
        {error && <div className="absolute inset-0 flex items-center justify-center text-rose-400 text-sm px-6 text-center">{error}</div>}
      </div>

      <div className="flex flex-wrap gap-2 justify-center max-w-[360px]">
        {STYLES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStyle(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              style === s
                ? 'border-cyan-400/70 text-cyan-300 bg-cyan-400/10'
                : 'border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 justify-center max-w-[360px]">
        {hooks.map((h) => (
          <button
            key={h.name}
            type="button"
            onClick={() => fire(h.name)}
            title={`${h.signature} — ${h.description}`}
            className="glow-btn px-3 py-1.5 text-xs rounded-lg font-mono"
          >
            {h.name.replace('efxOn', '')}
          </button>
        ))}
      </div>

      <div className="w-full max-w-[360px] rounded-lg border border-slate-700/60 bg-slate-900/60 px-3 py-2">
        <div className="text-xs text-slate-500 mb-1">window.__PLAYGEN_STATE.reward</div>
        <pre className="max-h-32 overflow-auto text-[10px] leading-4 text-amber-300/90 font-mono whitespace-pre-wrap">
          {reward ? JSON.stringify(reward, null, 1) : '（尚未发布，触发钩子后可见）'}
        </pre>
      </div>
    </div>
  );
}
